/**
 * The capture kit of the effects comparer (W3-04; Appendix C H5): a tiny Phira package that shows the test card and
 * turns one effect on halfway through, so a person can take the two screenshots the comparer needs from Phira itself:
 * one with the effect off ("before") and one with it on ("Phira").
 *
 * Contents (flat ZIP, ASCII names; P§2.1):
 *  - `chart.json`: RPE chart, 60 BPM (1 beat = 1 s), one judge line whose texture is the test card. At 1350 x 760 px
 *    with the default line scale (1 texture px = 1 RPE x unit; R§3.5) the card fills a 16:9 viewport. Alpha 255
 *    (a line without alpha events is invisible), every event list starts at beat 0 (F§15). Its one note is fake
 *    (never judged, no sound or hit effect; R§4.1), invisible (alpha 0), far outside the viewport and after the effect
 *    window (`KIT_TIMES.fakeNote`). It is a hedge: nobody has yet seen Phira play a chart without any note to its
 *    end (the Phira oracle does not run the game loop); step 2 of Appendix C H5 shows that the kit plays through.
 *    The hedge does not cover N = 0 scoring or combo maths (N = non-fake notes): the note is fake, so N stays 0. If
 *    Phira refuses to play a chart with no real notes, that is still a human finding from H5 step 2;
 *  - `extra.json`: the same BPM, and the effect from 0:16 to 0:40 (`KIT_TIMES`) with the comparer's values as vars
 *    (colours as integers 0..255, as Phira requires; P§5.1); global unless the comparer is set to the chart area;
 *  - `card.png` (texture and illustration), `silence.wav` (the song), `info.yml` (16:9 forced, so the viewport is
 *    the same on every screen), and `shaders/probe.glsl` for the custom UV probe.
 */

import type { BenchShader, RgbaImage } from './bench';
import { encodePng, silentWav, zip, type ZipEntry } from './encode';
import { CARD_KIT, testCard } from './testCard';

/** Timeline of the kit, in seconds (= beats at 60 BPM). */
export const KIT_TIMES = { bpm: 60, effectStart: 16, effectEnd: 40, fakeNote: 41, song: 42 } as const;

/** RPE x of the fake note: far outside the 16:9 viewport (x in -675..675). */
const FAKE_NOTE_X = 4000;

/** Package path of the custom shader (written as "/shaders/probe.glsl" in extra.json). */
export const KIT_SHADER_PATH = 'shaders/probe.glsl';

export interface KitSpec {
  readonly shader: BenchShader;
  /** Uniform values as the comparer uses them (a vec4 is a colour 0..1). */
  readonly vars: ReadonlyArray<readonly [name: string, value: number | readonly number[]]>;
  readonly global: boolean;
  /** Translated texts written into the package: the song name and the loading-screen tip. */
  readonly name: string;
  readonly tip: string;
}

type Triple = readonly [number, number, number];
const beat = (b: number): Triple => [Math.trunc(b), 0, 1];

/** A var value as extra.json writes it: float, [x, y], or a colour as [r, g, b, a] integers 0..255. */
export function extraVarValue(value: number | readonly number[]): number | number[] | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value.length === 2) return value.every(Number.isFinite) ? [...value] : null;
  if (value.length === 4)
    return value.map((c) => Math.round(Math.min(1, Math.max(0, Number.isFinite(c) ? c : 0)) * 255));
  return null;
}

export function kitExtraJson(spec: KitSpec): unknown {
  const vars: Record<string, number | number[]> = {};
  for (const [name, value] of spec.vars) {
    const v = extraVarValue(value);
    if (v !== null) vars[name] = v;
  }
  return {
    bpm: [{ time: beat(0), bpm: KIT_TIMES.bpm }],
    effects: [
      {
        start: beat(KIT_TIMES.effectStart),
        end: beat(KIT_TIMES.effectEnd),
        shader: spec.shader.kind === 'builtin' ? spec.shader.name : `/${KIT_SHADER_PATH}`,
        vars,
        global: spec.global,
      },
    ],
  };
}

function constEvent(value: number): unknown {
  return {
    startTime: beat(0),
    endTime: beat(KIT_TIMES.song),
    start: value,
    end: value,
    easingType: 1,
    easingLeft: 0.0,
    easingRight: 1.0,
    bezier: 0,
    bezierPoints: [0.0, 0.0, 0.0, 0.0],
    linkgroup: 0,
  };
}

/** The kit's one note: a fake, invisible Tap outside the viewport, after the effect (all fields required; R§2.2). */
function fakeNote(): unknown {
  return {
    above: 1,
    alpha: 0,
    endTime: beat(KIT_TIMES.fakeNote),
    isFake: 1,
    positionX: FAKE_NOTE_X,
    size: 1.0,
    speed: 1.0,
    startTime: beat(KIT_TIMES.fakeNote),
    type: 1,
    visibleTime: 999999.0,
    yOffset: 0.0,
  };
}

export function kitChartJson(name: string): unknown {
  return {
    META: {
      RPEVersion: 150,
      background: 'card.png',
      charter: 'Phiedit',
      composer: 'Phiedit',
      id: 'phiedit-effect-kit',
      level: 'NA Lv.0',
      name,
      offset: 0,
      song: 'silence.wav',
    },
    BPMList: [{ bpm: KIT_TIMES.bpm, startTime: beat(0) }],
    judgeLineGroup: ['Default'],
    judgeLineList: [
      {
        Group: 0,
        Name: 'card',
        Texture: 'card.png',
        anchor: [0.5, 0.5],
        bpmfactor: 1.0,
        father: -1,
        isCover: 1,
        isGif: false,
        // RPE counts real notes only.
        numOfNotes: 0,
        zOrder: 0,
        notes: [fakeNote()],
        eventLayers: [
          {
            alphaEvents: [constEvent(255)],
            moveXEvents: [constEvent(0)],
            moveYEvents: [constEvent(0)],
            rotateEvents: [constEvent(0)],
            speedEvents: [constEvent(10)],
          },
        ],
      },
    ],
  };
}

/** info.yml: every string double-quoted (JSON escapes are valid YAML), numbers and booleans plain (P§2.3). */
export function kitInfoYml(spec: Pick<KitSpec, 'name' | 'tip'>): string {
  const q = (s: string): string => JSON.stringify(s);
  return [
    `name: ${q(spec.name)}`,
    'difficulty: 0.0',
    `level: ${q('NA Lv.0')}`,
    `charter: ${q('Phiedit')}`,
    `composer: ${q('Phiedit')}`,
    `illustrator: ${q('Phiedit')}`,
    `chart: ${q('chart.json')}`,
    `format: ${q('rpe')}`,
    `music: ${q('silence.wav')}`,
    `illustration: ${q('card.png')}`,
    'previewStart: 0.0',
    'previewEnd: 10.0',
    'aspectRatio: 1.7777778',
    'forceAspectRatio: true',
    'backgroundDim: 0.6',
    `tip: ${q(spec.tip)}`,
    '',
  ].join('\n');
}

const utf8 = (s: string): Uint8Array => new TextEncoder().encode(s);

/** The kit's files, in archive order. `card` defaults to the kit-sized test card. */
export async function captureKitFiles(
  spec: KitSpec,
  card: RgbaImage = testCard(CARD_KIT.w, CARD_KIT.h),
): Promise<ZipEntry[]> {
  const files: ZipEntry[] = [
    { path: 'info.yml', bytes: utf8(kitInfoYml(spec)) },
    { path: 'chart.json', bytes: utf8(JSON.stringify(kitChartJson(spec.name))) },
    { path: 'extra.json', bytes: utf8(JSON.stringify(kitExtraJson(spec))) },
    { path: 'card.png', bytes: await encodePng(card) },
    { path: 'silence.wav', bytes: silentWav(KIT_TIMES.song) },
  ];
  if (spec.shader.kind === 'custom') files.push({ path: KIT_SHADER_PATH, bytes: utf8(spec.shader.source) });
  return files;
}

/** The kit as `.pez` bytes. */
export async function buildCaptureKit(spec: KitSpec, card?: RgbaImage): Promise<Uint8Array> {
  return zip(await captureKitFiles(spec, card));
}
